import { useTranslation } from 'react-i18next';
import { IconButton, DateTools, StatusBadge } from '@treezor/design-system';

import { usePaymentCardStatus } from '@/features/cards/hooks/usePaymentCardStatus';

import { IPaymentCard } from '@/features/cards/store/models/card.model';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

type TCardItemProps = {
  card: IPaymentCard;
  handleNavigate: (card: IPaymentCard) => void;
};

export function CardItem({ card, handleNavigate }: TCardItemProps) {
  const config = useDashboardConfig();
  const { t } = useTranslation();

  const { status, paymentCardStatusBadgeColor, paymentCardType } = usePaymentCardStatus(card);
  const cardDesign = card?.cardDesign
    ? config.settings?.cards?.designs?.find((cardDesign) => cardDesign.value.toString() === card?.cardDesign)?.label ||
      `${t('pages.currentProfiles.cards.card.createCard.steps.mainInformation.programId')} #${card.cardDesign}`
    : '';

  const columnCss = 'flex flex-col items-start text-secondary typo-body-regular-paragraph';

  return (
    <div className="flex flex-col items-start w-full bg-primary border-2 px-6 py-5 border-primary rounded-200 gap-3">
      <div className="flex items-start justify-between w-full">
        <div className="flex flex-col">
          <span className="typo-body-semibold-paragraph text-secondary">{card?.publicToken}</span>
          <span className="typo-body-regular-caption text-tertiary">{`ID #${card.cardId}  |  ${card?.maskedPan}`}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="typo-body-regular-caption text-tertiary">
            {`${cardDesign} - ${t(`pages.currentProfiles.cards.card.cardPaymentTypes.${paymentCardType}`)}`}
          </span>
          {card?.statusCode === 'LOCK_INTERNAL' ? (
            <span className="flex items-center justify-center rounded-100 px-3 py-2 text-onColor font-semibold text-[10px] bg-[#000000]">
              {t(`monetics.card.status.${status}`)}
            </span>
          ) : (
            <StatusBadge
              color={paymentCardStatusBadgeColor}
              label={t(`monetics.card.status.${status}`)}
              emphasis="low"
              size="s"
            />
          )}
        </div>
      </div>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-8">
          {card?.embossedName && (
            <div className={columnCss}>
              <span className="font-light">{t('pages.currentProfiles.profile.overview.embossedName')}</span>
              <span>{card.embossedName}</span>
            </div>
          )}
          {card?.isLive === 1 ? (
            <div className={columnCss}>
              <span className="font-light">{t('pages.currentProfiles.profile.overview.activatedOn')}</span>
              <span>{DateTools.format(card.startDate)}</span>
            </div>
          ) : (
            <>
              <div className={columnCss}>
                <span className="font-light">{t('pages.currentProfiles.profile.overview.activatedOn')}</span>
                <span>/</span>
              </div>
              <div className={columnCss}>
                <span className="font-light">{t('pages.currentProfiles.profile.overview.createdOn')}</span>
                <span>{DateTools.format(card.createdDate)}</span>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <IconButton icon="chevron_right" size="s" variant="secondary" onClick={() => handleNavigate(card)} />
        </div>
      </div>
    </div>
  );
}
